// The design lock for the manual: the first page of the Codex, written as
// the makers left it, in riddle and rhyme. Each page opens with the riddle a
// keeper would ask at the bridge, then the verse that answers it.
//
// Every verse must be TRUE to the engine. The plates are the proof: each
// shows real cards, and tests/unit/verses.test.ts plays every plate through
// src/domain/engine.ts. A plate that says two cards rest, rests; a fold
// folds; a mirror shows what the engine shows.
//
// The voice: old, short, chanted. No plain-word rule anywhere; the plates
// carry what the riddles hide.

import type { GlyphKey } from "../domain/rings.ts"
import type { IconId } from "./icons.ts"
import type { LawId } from "./laws.ts"

export type Plate =
  // cards in a row with a word under each: a legend
  | { readonly kind: "legend"; readonly cards: readonly GlyphKey[]; readonly captions: readonly string[] }
  // `upper` laid on `lower`: the engine must let it rest
  | { readonly kind: "rest"; readonly upper: GlyphKey; readonly lower: GlyphKey }
  // `upper` laid on `lower`: the engine must refuse it
  | { readonly kind: "refused"; readonly upper: GlyphKey; readonly lower: GlyphKey }
  // three laid from the bed outward fold into `made`
  | { readonly kind: "fold"; readonly three: readonly [GlyphKey, GlyphKey, GlyphKey]; readonly made: GlyphKey }
  // a card's truth, and what a mirror shows of it
  | { readonly kind: "mirror"; readonly truth: GlyphKey; readonly shown: GlyphKey }
  // free altars and empty columns, and how many cards a chain may carry
  | { readonly kind: "burden"; readonly altars: number; readonly columns: number; readonly carry: number }
  // marks of the table, as they appear above a column or on the map
  | { readonly kind: "marks"; readonly icons: readonly IconId[] }

export type PageId = "threes" | LawId | "lattice" | "help"

export type Page = {
  readonly id: PageId
  readonly title: string
  readonly riddle: string
  readonly verse: readonly string[]
  readonly plates: readonly Plate[]
}

export const MANUAL_TITLE = "The Laws, as the makers left them"

export const PAGES: readonly Page[] = [
  {
    id: "threes",
    title: "Here Be Threes",
    riddle: "Each ring may walk three roads. Which three?",
    verse: [
      "Each ring may rise, stay still, or fall,",
      "or fold: a band that holds them all.",
      "A bud the heart, a fork the second,",
      "a crown the third: so rings are reckoned.",
      "The rim gives hue: gold high, blue low;",
      "bone still; and banded, white as snow.",
      "The brow, in little marks, spells true,",
      "rim first, for cards half hid from view.",
    ],
    plates: [
      { kind: "legend", cards: ["+", "0", "-"], captions: ["risen", "still", "fallen"] },
      { kind: "legend", cards: ["+00", "0+0", "00+"], captions: ["the heart", "the second", "the rim"] },
    ],
  },
  {
    id: "rest",
    title: "Rest",
    riddle: "What lies on kin but not on twin?",
    verse: [
      "Lay kin on kin, alike in all",
      "save one ring: there the low stands tall;",
      "so risen bears the still; still bears",
      "the fallen. Skip, and rust declares.",
    ],
    plates: [
      { kind: "rest", upper: "0+0", lower: "++0" },
      { kind: "rest", upper: "-0", lower: "00" },
      { kind: "refused", upper: "-0", lower: "+0" },
    ],
  },
  {
    id: "empty",
    title: "The Empty Column",
    riddle: "What bed lies bare yet takes all in?",
    verse: [
      "A bare bed turns no card away,",
      "nor any chain you've strength to lay;",
      "and, lying bare, it doubles too",
      "the chains your hand may carry through.",
    ],
    plates: [],
  },
  {
    id: "run",
    title: "Runs",
    riddle: "When are the many only one?",
    verse: [
      "Cards resting each on that below",
      "they form one chain, as one they go:",
      "take off the top as deep you will;",
      "the chain goes whole and stays one still.",
    ],
    plates: [],
  },
  {
    id: "burden",
    title: "Burden",
    riddle: "How much may one hand bear across?",
    verse: [
      "The hand bears one; each altar bare",
      "adds one; a bare bed, twice the share.",
      "A bed left bare, of other kind,",
      "lets any chain cross, unconfined.",
    ],
    plates: [{ kind: "burden", altars: 1, columns: 1, carry: 4 }],
  },
  {
    id: "altar",
    title: "Altars",
    riddle: "What keeps one thing, and keeps it well?",
    verse: [
      "Above the beds the altars wait;",
      "each keeps one card, of any state,",
      "and gives it back to those who sought;",
      "a chain, an altar holds it not.",
    ],
    plates: [],
  },
  {
    id: "fold",
    title: "Fold",
    riddle: "Three enter, one comes out: what burns?",
    verse: [
      "Lay three from bed to brim, and show",
      "one ring go high, then still, then low;",
      "all else alike, the three are one:",
      "that ring's a band, the burning done.",
    ],
    plates: [{ kind: "fold", three: ["+0+", "00+", "-0+"], made: "*0+" }],
  },
  {
    id: "fold-high",
    title: "Folds of Folds",
    riddle: "What is a line made out of lines?",
    verse: [
      "What's folded folds again the same,",
      "when bands lie banded, frame to frame:",
      "three lines become a plane of light,",
      "three planes the whole, and all is white.",
    ],
    plates: [{ kind: "fold", three: ["*+0", "*00", "*-0"], made: "**0" }],
  },
  {
    id: "cascade",
    title: "Cascade",
    riddle: "What falls, though nothing has been dropped?",
    verse: [
      "One fold may wake another fold,",
      "and that the next, till all is told:",
      "a new-made card may make a three",
      "that burns in turn, no move from thee.",
    ],
    plates: [],
  },
  {
    id: "monad",
    title: "The Monad",
    riddle: "What is but one, and once was all?",
    verse: [
      "When all is folded into one,",
      "each ring a band, the rite is done.",
      "The Monad wakes, and round it play",
      "the cards it was: a ray, a ray.",
    ],
    plates: [{ kind: "fold", three: ["**+", "**0", "**-"], made: "***" }],
  },
  {
    id: "mirror",
    title: "Mirror Columns",
    riddle: "What lie do all the laws obey?",
    verse: [
      "Where water marks a column's head,",
      "each card lies turned within its bed:",
      "the risen sunk, the fallen high;",
      "the laws believe the lying eye.",
      "Pour chains across: reversed, they fall",
      "still chained, and weigh no weight at all.",
      "Lift one to rest on altar stone:",
      "the lie is gone, its truth is shown.",
    ],
    plates: [
      { kind: "marks", icons: ["mirror"] },
      { kind: "mirror", truth: "+0", shown: "-0" },
    ],
  },
  {
    id: "tide",
    title: "Tide Columns",
    riddle: "Who crowns the buried when three burn?",
    verse: [
      "Where tide coils high above a bed,",
      "each fold there turns it on its head:",
      "end over end, each card turned round;",
      "the deepest rises; chains stay sound.",
    ],
    plates: [{ kind: "marks", icons: ["tide"] }],
  },
  {
    id: "lattice",
    title: "The Lattice",
    riddle: "Where does each rite you play abide?",
    verse: [
      "The map's the cube, each point a rite",
      "that wakes when won ones near it light,",
      "each ring not still needs one. In line,",
      "three won will make the whole line shine.",
      "Nine won, a tablet folds and lies;",
      "the water wakes, then tides arise.",
      "When three are whole, the first rite's plan",
      "comes back: fold home where you began.",
    ],
    plates: [{ kind: "marks", icons: ["lattice"] }],
  },
  {
    id: "help",
    title: "The Oracle's Spark",
    riddle: "What help is here? What does it cost?",
    verse: [
      "The spark lights cards that may yet go,",
      "not where; strike twice, a ghost will show",
      "the next step on, or walk you back",
      "to where you lost the winning track.",
      "Turn up the two-script stone in hand:",
      "▲ is high, · is still, ▼ is low, ◆ a band.",
      "Take any aid, and lose the gold",
      "that only wins unhelped may hold.",
    ],
    plates: [{ kind: "marks", icons: ["glimmer", "rosetta"] }],
  },
]
